import {
  AGGRID_AUTOCOLUMN_ID,
  Flex,
  Icon,
  IconName,
  MobileDataTableGroupCardSurface,
  Text,
  type CreditBlotterExposure,
  type MobileGroupCardRendererProps,
} from '@talos/kyoko';
import type { ExpandedChangedEvent } from 'ag-grid-community';
import { useCallback, useEffect, useState, type KeyboardEvent, type ReactNode } from 'react';
import styled from 'styled-components';
import { VISIBLE_COLUMN_RENDER_OPTIONS } from './mobileCreditColumns';
import { getCreditGroupExposureMetric } from './mobileCreditMetric';
import { CreditWarningCell } from './styles';

export function MobileCreditGroupCard({
  context,
  node,
  renderCell,
}: MobileGroupCardRendererProps<CreditBlotterExposure>) {
  const [expanded, setExpanded] = useState(node.expanded);

  useEffect(() => {
    const listener = (event: ExpandedChangedEvent) => setExpanded(event.node.expanded);
    node.addEventListener('expandedChanged', listener);
    return () => node.removeEventListener('expandedChanged', listener);
  }, [node]);

  const onToggle = useCallback(() => node.setExpanded(!node.expanded), [node]);
  const onKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        onToggle();
      }
    },
    [onToggle]
  );

  const exposureMetric = getCreditGroupExposureMetric(renderCell);
  const marketAccount = renderMarketAccountName({ context, fallbackLabel: node.key ?? '', renderCell });
  const warning = renderCell('creditUsageWarning', VISIBLE_COLUMN_RENDER_OPTIONS);

  return (
    <MobileDataTableGroupCardSurface
      onClick={onToggle}
      onKeyDown={onKeyDown}
      role="button"
      tabIndex={0}
      aria-expanded={expanded}
      data-testid="mobile-credit-group-card"
      expanded={expanded}
    >
      <Flex alignItems="center" gap="spacing8" overflow="hidden" flex="1 1 0">
        <Icon
          icon={IconName.ChevronRight}
          rotate={expanded ? 90 : undefined}
          data-testid="mobile-credit-group-card-chevron"
        />
        <GroupMetric value={marketAccount} testId="mobile-credit-group-card-market-account" />
        {warning ? (
          <CreditWarningCell data-testid="mobile-credit-group-card-credit-usage-warning">{warning}</CreditWarningCell>
        ) : null}
      </Flex>
      <Flex alignItems="center" gap="spacing12" overflow="hidden">
        <GroupMetric
          label={exposureMetric.label}
          value={exposureMetric.value}
          testId={exposureMetric.testId}
          align="flex-end"
        />
      </Flex>
    </MobileDataTableGroupCardSurface>
  );
}

interface RenderMarketAccountNameParams {
  context: MobileGroupCardRendererProps<CreditBlotterExposure>['context'];
  fallbackLabel: string;
  renderCell: MobileGroupCardRendererProps<CreditBlotterExposure>['renderCell'];
}

function renderMarketAccountName({ context, fallbackLabel, renderCell }: RenderMarketAccountNameParams): ReactNode {
  const rendered = renderCell(AGGRID_AUTOCOLUMN_ID, VISIBLE_COLUMN_RENDER_OPTIONS);
  if (rendered != null && rendered !== false && rendered !== '') {
    return rendered;
  }

  const marketAccount = context.current.marketAccountsByName?.get(fallbackLabel);
  return marketAccount?.DisplayName ?? marketAccount?.Name ?? fallbackLabel;
}

interface GroupMetricProps {
  label?: string;
  value: ReactNode;
  testId: string;
  align?: 'flex-start' | 'flex-end';
}

function GroupMetric({ label, value, testId, align = 'flex-start' }: GroupMetricProps) {
  return (
    <GroupMetricWrapper alignItems={align}>
      {label ? (
        <Text color="colorTextSubtle" fontSize="fontSizeSm">
          {label}
        </Text>
      ) : null}
      <Flex data-testid={testId} color="colorTextImportant" className="group-metric-value">
        {value}
      </Flex>
    </GroupMetricWrapper>
  );
}

const GroupMetricWrapper = styled(Flex)`
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing2}px;
  overflow: hidden;

  &:has(.group-metric-value:empty) {
    display: none;
  }
`;
